import { useEffect, useState } from "react";
import { SharingApi, SharingError } from "../api";
import { isSafeToken } from "../../../types";

const PENDING_KEY = "todoit-pending-invite";
const INVITE_HASH = /^#\/invite\/([^/?#\s]+)$/;
export const OPEN_SHARED_EVENT = "todoit:open-shared";

// Move an invite token out of the URL and into sessionStorage, so it survives
// a login and never lingers in the address bar or history. Safe to call when
// logged out.
export const captureInviteHash = () => {
  const match = INVITE_HASH.exec(window.location.hash);
  if (!match || !isSafeToken(match[1])) return;
  window.sessionStorage.setItem(PENDING_KEY, match[1]);
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
};

// Read-and-remove in one step: the token is consumed synchronously, so a
// StrictMode double-run or a repeat hashchange can't spend it twice.
const takePendingToken = () => {
  const token = window.sessionStorage.getItem(PENDING_KEY);
  if (token) window.sessionStorage.removeItem(PENDING_KEY);
  return token;
};

type Notice = { kind: "ok" | "error"; text: string };

// Logged-in only. Accepts a pending invite once per token (no retries) and
// tells the shared-with-me panel to open the project.
export const InviteGate = () => {
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    const run = () => {
      captureInviteHash();
      const token = takePendingToken();
      if (!token) return;
      SharingApi.acceptInvite(token)
        .then((res) => {
          setNotice({ kind: "ok", text: "Project shared with you." });
          window.dispatchEvent(new CustomEvent(OPEN_SHARED_EVENT, { detail: res.parentId }));
        })
        .catch((e) => {
          const status = e instanceof SharingError ? e.status : 0;
          setNotice({
            kind: "error",
            text:
              status === 404
                ? "This invite is invalid or has expired."
                : status === 400
                  ? "You own this project."
                  : status === 409
                    ? "You have reached the maximum number of shared projects."
                    : "Couldn't accept the invite. Open the link again to retry.",
          });
        });
    };
    run();
    window.addEventListener("hashchange", run);
    return () => window.removeEventListener("hashchange", run);
  }, []);

  if (!notice) return null;
  return (
    <div
      className={`max-w-2xl mx-auto mt-2 px-3 py-2 rounded text-sm flex justify-between ${
        notice.kind === "ok" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
      }`}
    >
      <span>{notice.text}</span>
      <button onClick={() => setNotice(null)} className="text-xs">
        dismiss
      </button>
    </div>
  );
};
